import React, { useRef } from 'react';
import { Grid } from '@tmap/mmm-style-guide/src/Grid';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@tmap/mmm-style-guide/src/Icon';
import { IconButton } from '@tmap/mmm-style-guide/src/IconButton';
import { Typography } from '@tmap/mmm-style-guide/src/Typography';
import { styled } from '@tmap/mmm-style-guide/src/styled';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';

import imageUrlBuilder from '@sanity/image-url';
import client from '../client';

const builder = imageUrlBuilder(client);

const imgStyles = () => ({
  height: '60px',
  width: '150px',
  objectFit: 'scale-down'
})
const StyledPicture = styled('picture')(imgStyles);
const StyledImg = styled('img')(imgStyles);

const ArrowGrid = styled(Grid)(({ theme }) => ({
  zIndex: 1,
  [theme.breakpoints.down('lg')]: {
    display: 'none'
  }
}))
const SwiperGrid = styled(Grid, {
  shouldForwardProp: (p) => p !== 'slideCount'
})(({ theme, slideCount }) => ({
  width: '100%',

  [theme.breakpoints.up('lg')]: {
    marginLeft: theme.spacing(-8),
    marginRight: theme.spacing(-8),
    paddingLeft: theme.spacing(8),
    paddingRight: theme.spacing(8),
  },

  [theme.breakpoints.down('lg')]: {
    '& .swiper': {
      width: '100%',
      boxSizing: 'border-box',
      padding: theme.spacing(6),
      //offset the left edge to align it with the rest of page content on md
      paddingLeft: `10%`,
      [theme.breakpoints.down('md')]: {
        padding: theme.spacing(3),
      },
    }
  },

  '& .swiper-wrapper': {
    alignItems: 'center',
    [theme.breakpoints.up('lg')]: {
      ...(slideCount < 3 ? { justifyContent: 'center' } : {})
    }
  },

  '& .swiper-slide': {
    maxWidth: '150px',
    [theme.breakpoints.up('lg')]: {
      maxWidth: 'calc((100% - 96px) / 3)',
    },
    marginRight: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
}))

const OverflowingContentWrapper = styled('div')(({ theme }) => ({
  [theme.breakpoints.down('lg')]: {
    width: '100vw',
    display: 'inline-block',
    marginLeft: 'calc(50% - 50vw)',
  }
}))

const AwardItem = (props) => {
  const { award } = props;
  const { leadCopy, subLeadCopy, image, urlSource = '' } = award;
  // double actual for better quality
  const height = 120;
  const width = 300;
  return (
    <>
      <a href={urlSource} aria-label={image.alt || leadCopy} {...(urlSource.toLowerCase().indexOf('makemymove.com') < 0 ? { target: '_blank' } : {})}>
        <StyledPicture>
          <source srcSet={builder.image(image).format('webp').height(height).url()} />
          <StyledImg src={builder.image(image).height(height).url()}
            alt={image.alt || leadCopy}
            height={height}
            width={width}
          />
        </StyledPicture>
      </a>
      <Typography variant='body2' fontWeight={700} textAlign='center' fontSize='12px'>{leadCopy}</Typography>
      <Typography variant='body2' color='textSecondary' textAlign='center' fontSize='12px'>{subLeadCopy}</Typography>
    </>
  )
}

const CommunityAwards = (props) => {
  const { awards } = props;
  const swiperRef = useRef(null);

  return (
    <OverflowingContentWrapper>
      <Grid container direction='row' alignItems='center' justifyContent='center'>
        <ArrowGrid item>
          <IconButton variant='contained' size='large'
            onClick={() => swiperRef.current.swiper.slidePrev()}
            aria-label='Scroll Awards Back' >
            <KeyboardArrowLeft fontSize='large' />
          </IconButton>
        </ArrowGrid>
        <SwiperGrid item slideCount={awards.length}>
          <Swiper ref={swiperRef}
            slidesPerView='auto'
            modules={[Navigation]}
          >
            {awards.map((award, i) => (
              <SwiperSlide key={'award-' + i}>
                <AwardItem award={award} />
              </SwiperSlide>
            ))}
          </Swiper>
        </SwiperGrid>
        <ArrowGrid item>
          <IconButton variant='contained' size='large'
            onClick={() => swiperRef.current.swiper.slideNext()}
            aria-label='Scroll Awards Forward' >
            <KeyboardArrowRight fontSize='large' />
          </IconButton>
        </ArrowGrid>
      </Grid>
    </OverflowingContentWrapper>
  )
}

export default CommunityAwards;